// ============================================================
//  Page /questions — banque de questions
// ============================================================

import { useEffect, useState, useCallback } from "react";
import * as api from "../lib/api";
import { NIVEAUX, type Question, type QuestionInput } from "../lib/types";
import { QuestionForm } from "../components/QuestionForm";
import { QuestionImport } from "../components/QuestionImport";

const PAGE_SIZE = 25;

export function Questions() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ niveau: "", matiere: "", search: "" });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Question | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { total, questions } = await api.listQuestions({
        niveau: filters.niveau || undefined,
        matiere: filters.matiere || undefined,
        search: filters.search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setQuestions(questions);
      setTotal(total);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters.niveau, filters.matiere, filters.search, page]);

  useEffect(() => { reload(); }, [reload]);

  function onFilterChange(key: keyof typeof filters, value: string) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  }

  async function onCreate(input: QuestionInput) {
    await api.createQuestion(input);
    setShowForm(false);
    await reload();
  }

  async function onUpdate(id: string, input: QuestionInput) {
    await api.updateQuestion(id, input);
    setEditing(null);
    await reload();
  }

  async function onDelete(id: string) {
    if (!confirm("Supprimer cette question ?")) return;
    await api.deleteQuestion(id);
    await reload();
  }

  async function onImport(items: QuestionInput[]) {
    const r = await api.importQuestions(items);
    setShowImport(false);
    alert(`✓ ${r.inserted} question${r.inserted > 1 ? "s" : ""} importée${r.inserted > 1 ? "s" : ""}`);
    await reload();
  }

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="questions-page">
      <header className="questions-head">
        <div>
          <h1>Banque de questions</h1>
          <p className="questions-sub">{total} question{total > 1 ? "s" : ""} au total</p>
        </div>
        <div className="questions-actions">
          <button onClick={() => setShowImport(true)} className="btn-secondary">📥 Importer</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary">+ Nouvelle question</button>
        </div>
      </header>

      <div className="questions-filters">
        <select value={filters.niveau} onChange={(e) => onFilterChange("niveau", e.target.value)}>
          <option value="">Tous niveaux</option>
          {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <input
          type="text"
          placeholder="Matière (ex: Maths)"
          value={filters.matiere}
          onChange={(e) => onFilterChange("matiere", e.target.value)}
        />
        <input
          type="search"
          placeholder="Recherche dans le texte..."
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
        />
      </div>

      {error && <div className="page-error">Erreur : {error}</div>}
      {loading ? (
        <div className="page-loading">Chargement…</div>
      ) : questions.length === 0 ? (
        <div className="questions-empty">
          <p>Aucune question {filters.niveau || filters.matiere || filters.search ? "ne correspond aux filtres" : "encore"}.</p>
          {!filters.niveau && !filters.matiere && !filters.search && (
            <button onClick={() => setShowForm(true)} className="btn-primary">+ Créer la première</button>
          )}
        </div>
      ) : (
        <>
          <table className="questions-table">
            <thead>
              <tr>
                <th></th>
                <th>Question</th>
                <th>Réponses</th>
                <th>Niveau</th>
                <th>Matière</th>
                <th>Diff.</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id}>
                  <td className="cell-emoji">{q.emoji || "•"}</td>
                  <td className="cell-texte">
                    {q.texte}
                    {q.indice && <div className="cell-indice">💡 {q.indice}</div>}
                  </td>
                  <td className="cell-reponses">
                    {q.reponses.slice(0, 2).join(", ")}
                    {q.reponses.length > 2 && <span className="muted"> +{q.reponses.length - 2}</span>}
                  </td>
                  <td>{q.niveau && <span className="badge badge-niveau">{q.niveau}</span>}</td>
                  <td>{q.matiere && <span className="badge badge-matiere">{q.matiere}</span>}</td>
                  <td className="cell-diff">{"●".repeat(q.difficulte)}{"○".repeat(5 - q.difficulte)}</td>
                  <td className="cell-actions">
                    <button onClick={() => { setEditing(q); setShowForm(true); }} title="Éditer">✏️</button>
                    <button onClick={() => onDelete(q.id)} title="Supprimer">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {lastPage > 0 && (
            <div className="pagination">
              <button onClick={() => setPage(0)} disabled={page === 0}>« Début</button>
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>‹ Précédent</button>
              <span>Page {page + 1} / {lastPage + 1}</span>
              <button onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>Suivant ›</button>
              <button onClick={() => setPage(lastPage)} disabled={page >= lastPage}>Fin »</button>
            </div>
          )}
        </>
      )}

      {showForm && (
        <QuestionForm
          initial={editing}
          onSubmit={(input) => editing ? onUpdate(editing.id, input) : onCreate(input)}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {showImport && (
        <QuestionImport
          onImport={onImport}
          onCancel={() => setShowImport(false)}
        />
      )}
    </div>
  );
}
